import type { ReactNode } from 'react';
import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';
import type { HelpErrorTextsProps } from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';

import { useCallback, useEffect, useId, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { DeleteIcon } from '../../images/shapes';

import Button from '../Button/Button';
import FieldEditorsIndicator from '../FieldEditorsIndicator/FieldEditorsIndicator';
import HelpErrorTextsTemplate from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import RequiredTemplate from '../RequiredTemplate/RequiredTemplate';

interface RadioOption {
  label: ReactNode;
  value: string | number;
}

export type RadioChangeFn = (
  e: { target: { name: string; value: string | number } },
  value: string | number,
) => void;

export interface RadioGroupProps extends HelpErrorTextsProps {
  /**
   * List of options
   */
  options: RadioOption[];
  /**
   * Selected value
   */
  value?: string | number;
  /**
   * If radio group is disabled
   */
  disabled?: boolean;
  /**
   * If field is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * If radio group is required
   */
  required?: boolean;
  /**
   * Radio group name
   */
  name?: string;
  /**
   * On change callback
   */
  onChange?: RadioChangeFn;
  /**
   * Radio on blur handler
   */
  onBlur?: (e: { target: { name: string } }) => void;
  /**
   * Radio on focus handler
   */
  onFocus?: (e: { target: { name: string } }) => void;
  /**
   * If radio group should be shown horizontally
   */
  horizontal?: boolean;
  /**
   * Label for radio group
   */
  label?: ReactNode;
  /**
   * Editors data who are currently editing the field
   */
  editors?: WebsocketAwarenessState[];
  /**
   * Radio additional container classes
   */
  additionalClasses?: string;
  /**
   * Radio additional error classes
   */
  additionalRadioErrorClasses?: string;
  /**
   * Test id for radio group
   */
  testId?: string;
}

const RadioGroup = ({
  options,
  value = '',
  name = '',
  onChange,
  onBlur = EMPTY_FUNCTION,
  onFocus,
  horizontal = false,
  disabled = false,
  readonly = false,
  label = '',
  required = false,
  error = '',
  helpText = '',
  editors,
  additionalClasses = '',
  additionalRadioErrorClasses = '',
  testId = '',
}: RadioGroupProps) => {
  const componentId = useId();
  const [selected, setSelected] = useState(value);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const handleChange = useCallback(
    (value: string | number) => {
      if (disabled || readonly) return;
      setSelected(value);
      if (onChange) onChange({ target: { value, name } }, value);
    },
    [disabled, readonly, onChange, name],
  );

  const handleClear = useCallback(() => {
    handleChange('');
  }, [handleChange]);

  const handleBlur = useCallback(() => {
    onBlur({ target: { name } });
  }, [onBlur, name]);

  const renderOption = (option: RadioOption, idx: number) => {
    const checked = option.value === selected;
    const keyValue = `${option.value}-${idx}`;
    return (
      <div
        className={twMerge(
          'group/radio',
          horizontal ? 'inline-flex items-center mr-4' : 'flex mb-2',
        )}
        key={keyValue}
        {...getTestProps(testId, `${option.value}-container`)}
      >
        <input
          type="radio"
          id={`${componentId}-${idx}`}
          name={name}
          value={option.value}
          onChange={() => handleChange(option.value)}
          onBlur={handleBlur}
          checked={checked}
          className="appearance-none peer"
          required={required}
          disabled={disabled}
          readOnly={readonly}
          onFocus={onFocus}
          {...getTestProps(testId, `${option.value}-input`)}
        />
        <label
          htmlFor={`${componentId}-${idx}`}
          className={twMerge(
            'text-sm inline-flex dark:text-slate-200 flex gap-2',
            'peer-focus-visible:*:ring-2 peer-focus-visible:*:ring-blue',
            disabled || readonly ? 'cursor-not-allowed' : 'cursor-pointer',
            idx === -1 && 'text-red',
          )}
          {...getTestProps(testId, `${option.value}-label`)}
        >
          <FieldEditorsIndicator
            editors={checked ? editors : undefined}
            borderRadius={9999}
            position="center"
          >
            <div
              className={twMerge(
                'relative h-[18px] w-[18px] border border-slate-200 dark:border-slate-800 rounded-full',
                'peer-focus:ring-2 peer-focus:ring-blue-300 dark:group-focus/radio:ring-blue-600',
                disabled
                  ? 'bg-slate-100 dark:bg-slate-700'
                  : 'bg-white dark:bg-transparent',
                idx === -1 && 'border-red',
              )}
            >
              {checked && (
                <span
                  className={twMerge(
                    'h-2.5 w-2.5 rounded-full absolute m-[3px]',
                    disabled ? 'bg-slate-400 dark:bg-slate-800' : 'bg-blue-600',
                    idx === -1 && 'bg-red!',
                  )}
                  {...getTestProps(testId, 'checked')}
                />
              )}
            </div>
          </FieldEditorsIndicator>
          {option.label}
        </label>

        {checked && !required && !disabled && !readonly && (
          <Button
            buttonColor="borderless"
            iconImage={
              <DeleteIcon className="w-3 text-red hover:text-red-700" />
            }
            onClick={handleClear}
            additionalClasses="ml-2"
            {...getTestProps(testId, 'clear-selection', 'testId')}
          />
        )}
      </div>
    );
  };

  return (
    <div className={twMerge(additionalClasses)}>
      {label && (
        <div
          className="text-sm text-slate-400 dark:text-slate-200 mb-1"
          {...getTestProps(testId, 'label')}
        >
          {label}
          {required && <RequiredTemplate testId={testId} />}
        </div>
      )}
      {options.map(renderOption)}
      {value &&
        !options.find((option) => option.value === value) &&
        renderOption({ value, label: value }, -1)}
      <HelpErrorTextsTemplate
        helpText={helpText}
        error={error}
        additionalErrorClasses={additionalRadioErrorClasses}
        testId={testId}
      />
    </div>
  );
};

export default RadioGroup;
